/** User-facing copy when a mutation is refused because the property is inactive. */
export const INACTIVE_PROPERTY_USER_MESSAGE = "This property is no longer accepting offers.";

/** User-facing copy when the viewer lacks permission to persist calculator changes (e.g. admin on investor Deal Lens). */
export const DEAL_LENS_CALCULATOR_FORBIDDEN_MESSAGE =
  "This account can't save calculator changes on this investor's Deal Lens. Ask the investor to update the numbers, or sign in as them.";

const PROPERTY_INACTIVE_CODE = "PROPERTY_INACTIVE";

/**
 * @param {unknown} rawBody
 * @returns {{ code: string, message: string }}
 */
export function parseApiErrorFields(rawBody) {
  if (rawBody == null) return { code: "", message: "" };

  if (typeof rawBody === "object" && !Array.isArray(rawBody)) {
    const code = String(rawBody.code ?? "").trim();
    const message =
      rawBody.message != null
        ? String(rawBody.message).trim()
        : rawBody.error != null
          ? String(rawBody.error).trim()
          : "";
    return { code, message };
  }

  if (typeof rawBody !== "string") return { code: "", message: "" };

  const trimmed = rawBody.trim();
  if (!trimmed) return { code: "", message: "" };

  if (trimmed.startsWith("{")) {
    try {
      return parseApiErrorFields(JSON.parse(trimmed));
    } catch {
      // Fall through for non-JSON map strings.
    }
  }

  const codeMatch = trimmed.match(/(?:^|[,{]\s*)code=([^,}]+)/i);
  const messageMatch = trimmed.match(/(?:^|[,{]\s*)message=([^,}]+)/i);
  return {
    code: codeMatch?.[1]?.trim() ?? "",
    message: messageMatch?.[1]?.trim() ?? "",
  };
}

/**
 * @param {unknown} rawBody
 * @returns {boolean}
 */
export function isPropertyInactiveApiFailure(rawBody) {
  const { code } = parseApiErrorFields(rawBody);
  return code.toUpperCase() === PROPERTY_INACTIVE_CODE;
}

/**
 * @param {unknown} rawBody
 * @returns {boolean}
 */
export function isForbiddenApiFailure(rawBody) {
  if (rawBody != null && typeof rawBody === "object" && !Array.isArray(rawBody)) {
    const status = rawBody.statusCode ?? rawBody.status;
    if (String(status) === "403") return true;
  }
  const { code, message } = parseApiErrorFields(rawBody);
  const combined = `${code} ${message}`.trim().toLowerCase();
  if (combined.includes("forbidden") || combined.includes("access denied")) return true;
  if (typeof rawBody === "string" && rawBody.trim().toLowerCase().includes("forbidden")) {
    return true;
  }
  return false;
}

/**
 * @param {unknown} rawBodyOrMessage
 * @param {string} [resolvedMessage]
 * @returns {boolean}
 */
export function shouldSyncPropertyInactiveFromFailure(rawBodyOrMessage, resolvedMessage = "") {
  if (isPropertyInactiveApiFailure(rawBodyOrMessage)) return true;
  const resolved = String(resolvedMessage ?? "").trim();
  if (resolved === INACTIVE_PROPERTY_USER_MESSAGE) return true;
  if (typeof rawBodyOrMessage === "string" && rawBodyOrMessage.trim() === INACTIVE_PROPERTY_USER_MESSAGE) {
    return true;
  }
  return false;
}

/**
 * @param {unknown} rawBody
 * @param {string} [fallback]
 * @returns {string}
 */
export function resolveUserFacingApiFailureMessage(rawBody, fallback = "Something went wrong. Please try again.") {
  if (isPropertyInactiveApiFailure(rawBody)) {
    return INACTIVE_PROPERTY_USER_MESSAGE;
  }
  if (isForbiddenApiFailure(rawBody)) {
    return DEAL_LENS_CALCULATOR_FORBIDDEN_MESSAGE;
  }

  if (rawBody == null) return fallback;
  if (typeof rawBody === "object" && !Array.isArray(rawBody)) {
    const { message } = parseApiErrorFields(rawBody);
    if (message) return message;
    if (rawBody.error != null) return String(rawBody.error);
    return fallback;
  }
  if (typeof rawBody !== "string") return fallback;

  const trimmed = rawBody.trim();
  if (!trimmed) return fallback;
  if (trimmed.startsWith("{")) {
    try {
      return resolveUserFacingApiFailureMessage(JSON.parse(trimmed), fallback);
    } catch {
      // Fall through for non-JSON map strings.
    }
  }

  const { message } = parseApiErrorFields(trimmed);
  if (message) return message;
  return trimmed;
}

/**
 * @param {{ propertyDetailsStore?: { markPropertyDeactivated?: Function }, opportunitiesStore?: { markDealsInactiveForProperty?: Function } }} stores
 * @param {{ customer?: string | null, propertyId?: string | null }} params
 */
export function applyPropertyInactiveToClientCaches(stores, { customer, propertyId }) {
  const cid = String(customer ?? "").trim();
  const pid = String(propertyId ?? "").trim();
  if (!cid || !pid) return;

  stores?.propertyDetailsStore?.markPropertyDeactivated?.(cid, pid);
  stores?.opportunitiesStore?.markDealsInactiveForProperty?.(pid);
}
